/**
 * TrackOtic Auth Context — cookie-only session.
 *
 * The server sets an HttpOnly `trackotic_session` cookie on /login and on OTP verify;
 * the SPA never receives, stores or forwards a bearer token (no localStorage, no memory copy).
 * Every call goes out with `credentials: 'include'` / axios `withCredentials`.
 * `getBearerToken()` / `authConfig()` are kept as no-op shims for older call sites.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const TrackOticAuthContext = createContext(null);
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Send cookies on every axios call.
axios.defaults.withCredentials = true;

export function getBearerToken() {
  return null;
}

export function authConfig(extra = {}) {
  return extra;
}

export function TrackOticAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [scopeSummary, setScopeSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/v1/auth/me`, authConfig());
      const { user: userData, tenant: tenantData, permissions: perms, roles: userRoles, scope_summary } = response.data;
      setUser(userData);
      setTenant(tenantData);
      setPermissions(perms || []);
      setRoles(userRoles || []);
      setScopeSummary(scope_summary);
      return userData;
    } catch (_error) {
      // 401 just means "no valid session" — quietly leave state empty.
      setUser(null);
      setTenant(null);
      setPermissions([]);
      setRoles([]);
      setScopeSummary(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // One-shot cleanup of pre-migration localStorage tokens.
    try {
      localStorage.removeItem('trackotic_token');
      localStorage.removeItem('trackotic_user');
      localStorage.removeItem('trackotic_tenant');
      localStorage.removeItem('ops_token');
      localStorage.removeItem('token');
    } catch (_e) { /* SSR / private mode safe-guard */ }
    fetchUserInfo();
  }, [fetchUserInfo]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_BASE}/api/v1/auth/login`, { email, password });
    const { user: userData, tenant: tenantData } = response.data;   // cookie-only: the body token is ignored by the SPA
    setUser(userData);
    setTenant(tenantData);
    await fetchUserInfo();
    return { user: userData, tenant: tenantData };
  };

  // Cookie-only session: the server has already set the HttpOnly cookie; the SPA never holds a token.
  const applySessionUser = async (userData, tenantData) => {
    setUser(userData);
    setTenant(tenantData);
    await fetchUserInfo();
    return { user: userData, tenant: tenantData };
  };

  // Back-compat shim for callers that still pass a token as the first arg (Phase 38 migration).
  const applyTokenAndUser = async (_ignoredToken, userData, tenantData) => applySessionUser(userData, tenantData);

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/api/v1/auth/logout`, {}, authConfig());
    } catch (_e) { /* network failure shouldn't trap the user */ }
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setTenant(null);
    setPermissions([]);
    setRoles([]);
    setScopeSummary(null);
  };

  const hasPermission = (permissionCode) =>
    permissions.includes(permissionCode) || permissions.includes('admin');

  const hasAnyPermission = (permissionCodes) =>
    permissionCodes.some((code) => hasPermission(code));

  const isTrack0tic = () => tenant?.code === 'TRACK0TIC';
  const hasTenantWideAccess = () => scopeSummary?.has_tenant_wide_access || false;

  return (
    <TrackOticAuthContext.Provider value={{
      user,
      tenant,
      token: null,
      loading,
      permissions,
      roles,
      scopeSummary,
      login,
      applySessionUser,
      applyTokenAndUser,
      logout,
      hasPermission,
      hasAnyPermission,
      isTrack0tic,
      hasTenantWideAccess,
      refreshUser: fetchUserInfo,
    }}>
      {children}
    </TrackOticAuthContext.Provider>
  );
}

export const useTrackOticAuth = () => {
  const context = useContext(TrackOticAuthContext);
  if (!context) {
    throw new Error('useTrackOticAuth must be used within an TrackOticAuthProvider');
  }
  return context;
};
